/**
 * Funciones helper para el módulo de Control Semanal
 */

import { CHILE_TIMEZONE_OFFSET } from './constants';

/**
 * Formatear fecha para display en formato chileno
 * @param {string} dateString - Fecha en formato ISO (YYYY-MM-DD)
 * @returns {string} Fecha formateada (ej: "lun 7 oct")
 */
export const formatDate = (dateString) => {
  if (!dateString) return '';

  // Crear fecha en zona horaria chilena
  const date = new Date(dateString + 'T12:00:00'); // Mediodía para evitar problemas de zona horaria

  return date.toLocaleDateString('es-CL', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'America/Santiago'
  });
};

/**
 * Calcular el rango de fechas inicial (últimos 7 días)
 * @returns {{ startDate: string, endDate: string }}
 */
export const calculateInitialDateRange = () => {
  const today = new Date();
  const chileTime = new Date(today.getTime() + (CHILE_TIMEZONE_OFFSET * 60 * 1000));

  const endDate = new Date(chileTime);
  const startDate = new Date(chileTime);
  startDate.setDate(chileTime.getDate() - 6); // 7 días incluyendo hoy

  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0]
  };
};

/**
 * Obtener fecha actual en zona horaria de Chile
 * @returns {string} Fecha en formato YYYY-MM-DD
 */
export const getChileToday = () => {
  const today = new Date();
  const chileTime = new Date(today.getTime() + (CHILE_TIMEZONE_OFFSET * 60 * 1000));
  return chileTime.toISOString().split('T')[0];
};

/**
 * Calcular columnas de fechas para el rango dado
 * @param {string} startDate - Fecha inicial (YYYY-MM-DD)
 * @param {string} endDate - Fecha final (YYYY-MM-DD)
 * @returns {string[]} Array de fechas
 */
export const getDateColumns = (startDate, endDate) => {
  if (!startDate || !endDate) return [];

  try {
    const columns = [];
    const start = new Date(startDate + 'T00:00:00');
    const end = new Date(endDate + 'T00:00:00');

    for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
      columns.push(date.toISOString().split('T')[0]);
    }

    return columns;
  } catch (error) {
    console.error('Error al calcular columnas de fecha:', error);
    return [];
  }
};

/**
 * Obtener total de guías por estado en toda la semana
 * @param {Object} weeklyData - Datos semanales
 * @param {string} stateKey - Clave del estado
 * @returns {number} Total de guías
 */
export const getStateTotal = (weeklyData, stateKey) => {
  let total = 0;
  Object.values(weeklyData).forEach(dateData => {
    total += dateData[stateKey] || 0;
  });
  return total;
};

/**
 * Obtener total de guías por fecha
 * @param {Object} weeklyData - Datos semanales
 * @param {string} dateKey - Clave de la fecha
 * @returns {number} Total de guías
 */
export const getDateTotal = (weeklyData, dateKey) => {
  let total = 0;
  Object.values(weeklyData[dateKey] || {}).forEach(count => {
    total += count;
  });
  return total;
};

/**
 * Procesar datos de recepción diaria para la tabla
 * @param {Array} data - Datos crudos del API
 * @returns {{ processedData: Object, states: string[] }}
 */
export const processWeeklyData = (data) => {
  const processedData = {};
  const statesSet = new Set();

  // Inicializar estructura de datos
  data.forEach(day => {
    const dateKey = day.fecha;
    processedData[dateKey] = {};

    // Agrupar por estado principal, no por subestado
    const stateGroups = {};
    day.subestados.forEach(subestado => {
      const stateKey = subestado.estado;
      if (!stateGroups[stateKey]) {
        stateGroups[stateKey] = 0;
      }
      stateGroups[stateKey] += subestado.cantidad;
    });

    // Asignar los totales por estado
    Object.keys(stateGroups).forEach(stateKey => {
      statesSet.add(stateKey);
      processedData[dateKey][stateKey] = stateGroups[stateKey];
    });
  });

  // Ordenar estados con "delivered" primero
  const sortedStates = Array.from(statesSet).sort((a, b) => {
    if (a === 'delivered') return -1;
    if (b === 'delivered') return 1;
    return a.localeCompare(b);
  });

  return {
    processedData,
    states: sortedStates
  };
};

/**
 * Verificar si se puede navegar hacia adelante
 * @param {string} endDate - Fecha final del rango actual
 * @returns {boolean}
 */
export const canNavigateNext = (endDate) => {
  if (!endDate) return false;

  const chileToday = getChileToday();

  try {
    const nextEnd = new Date(endDate + 'T00:00:00');
    nextEnd.setDate(nextEnd.getDate() + 7);
    return nextEnd.toISOString().split('T')[0] <= chileToday;
  } catch (error) {
    console.error('Error al calcular navegación:', error);
    return false;
  }
};

/**
 * Navegar fechas (adelante o atrás)
 * @param {Object} dateRange - Rango de fechas actual
 * @param {string} direction - Dirección ('prev' o 'next')
 * @returns {Object|null} Nuevo rango de fechas o null si hay error
 */
export const navigateDates = (dateRange, direction) => {
  if (!dateRange.startDate || !dateRange.endDate) return null;

  try {
    const currentStart = new Date(dateRange.startDate + 'T00:00:00');
    const currentEnd = new Date(dateRange.endDate + 'T00:00:00');

    if (direction === 'prev') {
      currentStart.setDate(currentStart.getDate() - 1);
      currentEnd.setDate(currentEnd.getDate() - 1);
    } else if (direction === 'next') {
      const chileToday = getChileToday();
      const nextEnd = new Date(currentEnd);
      nextEnd.setDate(nextEnd.getDate() + 1);

      if (nextEnd.toISOString().split('T')[0] > chileToday) {
        return null;
      }

      currentStart.setDate(currentStart.getDate() + 1);
      currentEnd.setDate(currentEnd.getDate() + 1);
    }

    return {
      startDate: currentStart.toISOString().split('T')[0],
      endDate: currentEnd.toISOString().split('T')[0]
    };
  } catch (error) {
    console.error('Error al navegar fechas:', error);
    return null;
  }
};
